// Central leveled logger + funnel-failure event helper.
//
// Two jobs:
//   1. log.debug/info/warn/error — a consistent, prefixed console surface with
//      a single mute switch (?quiet in the URL). Errors always print.
//   2. log.event(name, payload) — routes a named FAILURE/funnel event to the
//      analytics pipeline (PostHog ring buffer via getAnalytics) so production
//      problems are visible in the dashboard, not just the console.
//
// This is the seam for the eventual full console.* consolidation. New code and
// the key failure paths route through here; the remaining scattered console.*
// calls can be migrated incrementally without touching this surface.

import { getAnalytics, type AnalyticsEventName } from '../playcanvas/analytics';

const PREFIX = '[imersivo]';

function quiet(): boolean {
  try {
    return new URLSearchParams(window.location.search).has('quiet');
  } catch {
    return false;
  }
}

export const log = {
  debug(...args: unknown[]): void {
    if (!quiet()) console.debug(PREFIX, ...args);
  },
  info(...args: unknown[]): void {
    if (!quiet()) console.info(PREFIX, ...args);
  },
  warn(...args: unknown[]): void {
    console.warn(PREFIX, ...args);
  },
  error(...args: unknown[]): void {
    console.error(PREFIX, ...args);
  },
  /**
   * Record a named failure/funnel event: console.warn for local visibility +
   * forward to analytics so it aggregates in the dashboard. Analytics never
   * throws into the caller.
   */
  event(name: AnalyticsEventName, payload?: Record<string, unknown>): void {
    console.warn(`${PREFIX} ${name}`, payload ?? '');
    try {
      getAnalytics().log(name, payload);
    } catch {
      /* analytics must never crash the calling path */
    }
  },
};
